import { X } from '@ui/components/icons';
import {
  getChainInfo,
  getChainName,
  SUPPORTED_CHAIN_IDS,
} from '@ui/constants/chain';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { ChainId } from '@ui/types/chain';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';
import { Divider, HStack, styled } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import { useDisconnectWallet } from '../hooks/useDisconnectWallet';

export const NetworkSwitchModal = ({
  children,
  title,
  switchChain,
}: {
  title: string;
  switchChain: (chain: ChainId) => void;
  children: ReactElement;
}) => {
  const { t } = useTranslation();
  const disconnectWallet = useDisconnectWallet();
  const { isConnected } = useAccount();

  return (
    <Dialog.Root size="small" lazyMount unmountOnExit modal={false}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content minW="72" data-cy="NetworkSwitchModal">
            <Dialog.Title asChild>
              <HStack textStyle="headings.large" justifyContent="space-between">
                <Text>{title}</Text>

                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </HStack>
            </Dialog.Title>
            <Dialog.Context>
              {(ctx) => (
                <Dialog.Description
                  px="4"
                  py={4}
                  gap={5}
                  display="flex"
                  flexDir="column"
                >
                  {SUPPORTED_CHAIN_IDS.map((chainId) => (
                    <Button
                      variant="ghost"
                      _loading={{
                        opacity: 0.5,
                      }}
                      key={chainId}
                      _hover={{
                        cursor: 'pointer',
                      }}
                      gap="4"
                      px={2}
                      py={3}
                      h="auto"
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      w="full"
                      onClick={() => {
                        switchChain(chainId);
                        ctx.setOpen(false);
                      }}
                      data-cy={`ChainButton-${getChainName(chainId)}`}
                    >
                      <styled.img
                        src={getChainInfo(chainId).icon}
                        boxSize="6"
                      />
                      <Text textAlign="start" textStyle="text.xlarge">
                        {getChainName(chainId)}
                      </Text>
                    </Button>
                  ))}

                  {isConnected && (
                    <>
                      <HStack>
                        <Divider />
                        <Text
                          color="greyWhite.lightGrey3"
                          textStyle="text.xxsmall"
                        >
                          or
                        </Text>
                        <Divider />
                      </HStack>
                      <Button
                        variant="text"
                        color="price.priceDown"
                        onClick={() => {
                          disconnectWallet();
                        }}
                        justifyContent="flex-start"
                        ml="8px"
                      >
                        {t('common.Disconnect Wallet')}
                        <Icon boxSize="12px">
                          <LogOut />
                        </Icon>
                      </Button>
                    </>
                  )}
                </Dialog.Description>
              )}
            </Dialog.Context>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
